import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./contrat.css";

const Icon = ({ size = 18, sw = 1.6, children, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ContractIcon = () => (
  <Icon>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </Icon>
);
const EyeIcon = () => (
  <Icon size={14} sw={1.8}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
const SpinnerIcon = () => (
  <Icon size={14} sw={2} className="wc-spinner"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></Icon>
);
const LockIcon = () => (
  <Icon size={12} sw={1.8}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Icon>
);

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const Skeleton = () => (
  <div className="wc-skeleton" aria-hidden="true">
    {[0, 1, 2].map((i) => <div key={i} className="wc-skel-row" />)}
  </div>
);

const WalletContracts = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [openingPdf, setOpeningPdf] = useState(null);

  const fetchContracts = useCallback(async (signal) => {
    if (!userId) {
      setError("We couldn't find your account. Please log in again.");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/contracts/user/${userId}`, {
        signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      const list = res.data.contracts || res.data || [];
      setContracts(
        (Array.isArray(list) ? list : []).sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        )
      );
    } catch (err) {
      if (axios.isCancel(err)) return;
      console.error("Error loading contracts:", err);
      setError("Your contracts didn't load. Check your connection and try again.");
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [API_URL, userId, token]);

  useEffect(() => {
    const c = new AbortController();
    fetchContracts(c.signal);
    return () => c.abort();
  }, [fetchContracts]);

  const openContract = async (contractId) => {
    if (!contractId || openingPdf) return;
    setOpeningPdf(contractId);

    // On ouvre l'onglet tout de suite (dans le clic) : sinon le bloqueur
    // de popups le refuse après l'attente réseau.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;

    try {
      const response = await axios.get(`${API_URL}/contracts/view/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 30000,
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (tab) tab.location.href = url;
      else window.location.assign(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Error opening PDF:", err);
      if (tab) tab.close();
      toast.error("The contract didn't open. Try again.", { position: "top-center" });
    } finally {
      setOpeningPdf(null);
    }
  };

  if (isLoading)
    return (
      <div className="wc-container" role="status" aria-live="polite" aria-label="Loading contracts">
        <Skeleton />
      </div>
    );

  if (error)
    return (
      <div className="wc-container">
        <div className="wc-state wc-state--error" role="alert">
          <i className="wc-state__icon" aria-hidden="true">!</i>
          <p className="wc-state__text">Contracts unavailable</p>
          <p className="wc-state__sub">{error}</p>
          <button type="button" className="wc-retry" onClick={() => fetchContracts()}>Try again</button>
        </div>
      </div>
    );

  if (contracts.length === 0)
    return (
      <div className="wc-container">
        <div className="wc-state">
          <i className="wc-state__icon" aria-hidden="true"><ContractIcon /></i>
          <p className="wc-state__text">No contracts yet</p>
          <p className="wc-state__sub">Your signed contracts will appear here.</p>
        </div>
      </div>
    );

  return (
    <div className="wc-container">
      <header className="wc-header">
        <h2 className="wc-title">Contracts</h2>
        <p className="wc-count">
          {contracts.length} contract{contracts.length > 1 ? "s" : ""}
        </p>
      </header>

      <ul className="wc-list">
        {contracts.map((c) => {
          const busy = openingPdf === c._id;
          return (
            <li key={c._id}>
              <button
                type="button"
                className="wc-item"
                onClick={() => openContract(c._id)}
                disabled={busy}
                aria-label={`Open contract from ${formatDate(c.createdAt)}`}
              >
                <span className="wc-icon-wrap"><ContractIcon /></span>
                <span className="wc-meta">
                  <span className="wc-date">{formatDate(c.createdAt)}</span>
                  <span className="wc-sub">Contract document</span>
                </span>
                <span className="wc-action">
                  {busy ? <SpinnerIcon /> : <EyeIcon />}
                  {busy ? "Opening…" : "View"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="wc-footer">
        <LockIcon />
        The PDF opens in a new tab.
      </p>
    </div>
  );
};

export default WalletContracts;